import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Form, Input, Segmented, Spin, Typography, message } from 'antd';
import { ArrowLeftOutlined, CheckOutlined, SafetyCertificateOutlined, ShopOutlined } from '@ant-design/icons';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { ENDPOINTS } from '../helpers/api/apiConfig';
import loginImage from '../static/ColourBrandPrimary2x.png';
import '../style/Login.css';
import '../style/CompanyCreation.css';

const { Title, Text } = Typography;

type PlanCode = 'trial' | 'solo' | 'lite' | 'basic' | 'pro';
type BillingCycle = 'monthly' | 'annual';

interface PlanOption {
    code: PlanCode;
    name: string;
    description: string;
    annualPriceGbp: number | null;
    features: string[];
    footnote: string;
    trialDurationDays?: number | null;
}

const MONTHLY_UPLIFT_FACTOR = 1.1;

const PLAN_CATALOG: Record<PlanCode, PlanOption> = {
    trial: {
        code: 'trial',
        name: 'OSCAR Trial',
        description: 'Explore the core workflows before choosing a paid plan.',
        annualPriceGbp: null,
        features: ['Immediate access', 'AI assistant included', 'Area and measurement workflows'],
        footnote: 'No payment required',
    },
    solo: {
        code: 'solo',
        name: 'OSCAR Solo',
        description: 'AI-assisted logging for independent professionals.',
        annualPriceGbp: 240,
        features: ['1 entity', 'Up to 5 areas', '1.2M AI tokens per month'],
        footnote: 'For independent operators',
    },
    lite: {
        code: 'lite',
        name: 'OSCAR Lite',
        description: 'Calibrated Lightbox intelligence for individual professionals.',
        annualPriceGbp: 900,
        features: ['1 user and 1 entity', 'Up to 5 areas', 'Lightbox integration'],
        footnote: 'Hardware integration enabled',
    },
    basic: {
        code: 'basic',
        name: 'OSCAR Basic',
        description: 'Professional team intelligence for a single operation.',
        annualPriceGbp: 1800,
        features: ['Multiple users', 'Up to 20 areas', 'Lightbox integration'],
        footnote: 'Best for growing teams',
    },
    pro: {
        code: 'pro',
        name: 'OSCAR Pro',
        description: 'Multi-entity intelligence for contractors and venue groups.',
        annualPriceGbp: 2520,
        features: ['Unlimited entities', 'Up to 40 areas', 'Cross-entity intelligence'],
        footnote: 'For complex operations',
    },
};

const FALLBACK_PLANS = Object.values(PLAN_CATALOG);

const formatCurrencyGbp = (amount: number) => `£${amount.toFixed(2).replace(/\.00$/, '')}`;

const getMonthlyPrice = (plan: PlanOption, billingCycle: BillingCycle) => {
    if (!plan.annualPriceGbp) return null;
    const annualTotal = billingCycle === 'annual'
        ? plan.annualPriceGbp
        : plan.annualPriceGbp * MONTHLY_UPLIFT_FACTOR;
    return Number((annualTotal / 12).toFixed(2));
};

const CompanyCreation: React.FC = () => {
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const [plans, setPlans] = useState<PlanOption[]>([]);
    const [plansLoading, setPlansLoading] = useState(true);
    const [selectedPlan, setSelectedPlan] = useState<PlanCode | null>(null);
    const [billingCycle, setBillingCycle] = useState<BillingCycle>('annual');
    const [step, setStep] = useState<'plan' | 'company' | 'complete'>('plan');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    const selectedPlanOption = useMemo(
        () => plans.find((plan) => plan.code === selectedPlan) || null,
        [plans, selectedPlan]
    );

    useEffect(() => {
        let cancelled = false;
        const loadPlans = async () => {
            try {
                const response = await axios.get(ENDPOINTS.ACTIVE_SUBSCRIPTION_PLANS, { withCredentials: true });
                const available = (Array.isArray(response.data?.data) ? response.data.data : [])
                    .map((plan: any) => {
                        const code = String(plan.code || '').toLowerCase() as PlanCode;
                        const presentation = PLAN_CATALOG[code];
                        if (!presentation) return null;
                        return {
                            ...presentation,
                            name: String(plan.name || presentation.name),
                            description: String(plan.description || presentation.description),
                            trialDurationDays: plan.trialDurationDays ?? null,
                        };
                    })
                    .filter(Boolean) as PlanOption[];
                if (!cancelled) setPlans(available.length > 0 ? available : FALLBACK_PLANS);
            } catch (_error) {
                if (!cancelled) setPlans(FALLBACK_PLANS);
            } finally {
                if (!cancelled) setPlansLoading(false);
            }
        };

        loadPlans();
        document.body.classList.add('login-page');
        return () => {
            cancelled = true;
            document.body.classList.remove('login-page');
        };
    }, []);

    const selectPlan = (code: PlanCode) => {
        setSelectedPlan(code);
        setError('');
    };

    const continueWithPlan = () => {
        if (!selectedPlan) {
            setError('Choose a plan to continue.');
            return;
        }
        setError('');
        form.resetFields();
        form.setFieldsValue({ companyName: '', companyAddress: '' });
        setStep('company');
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const createCompany = async (values: { companyName: string; companyAddress?: string }) => {
        if (!selectedPlan) return;
        setSubmitting(true);
        setError('');
        try {
            const response = await axios.post(
                ENDPOINTS.CREATE_OWN_COMPANY,
                {
                    companyName: String(values.companyName || '').trim(),
                    companyAddress: String(values.companyAddress || '').trim() || undefined,
                    planChoice: selectedPlan,
                    billingCycle: selectedPlan === 'trial' ? undefined : billingCycle,
                },
                { withCredentials: true }
            );

            const companyId = response.data?.company?.id;
            if (response.data?.flow === 'payment_required' && companyId) {
                const successUrl = encodeURIComponent('/select-company?subscription=success');
                const cancelUrl = encodeURIComponent('/select-company?subscription=pending');
                navigate(
                    `/subscription-checkout?flow=upgrade&companyId=${companyId}&plan=${selectedPlan}` +
                    `&cycle=${billingCycle}&successUrl=${successUrl}&cancelUrl=${cancelUrl}`,
                    { replace: true }
                );
                return;
            }

            setStep('complete');
            message.success(response.data?.message || 'Company created successfully.');
        } catch (err: any) {
            setError(err?.response?.data?.message || err?.response?.data?.error || 'Company creation failed.');
        } finally {
            setSubmitting(false);
        }
    };

    const renderPlanCard = (plan: PlanOption) => {
        const isSelected = selectedPlan === plan.code;
        const monthlyPrice = getMonthlyPrice(plan, billingCycle);
        const trialDays = plan.trialDurationDays || 7;

        return (
            <div
                key={plan.code}
                className={`company-plan-card company-plan-card--${plan.code}${isSelected ? ' company-plan-card--selected' : ''}`}
                role="radio"
                aria-checked={isSelected}
                tabIndex={0}
                onClick={() => selectPlan(plan.code)}
                onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        selectPlan(plan.code);
                    }
                }}
            >
                {plan.code === 'basic' && <span className="company-plan-popular">Most popular</span>}
                <div className="company-plan-card-topline">
                    <span className={`company-plan-code company-plan-code--${plan.code}`}>{plan.code}</span>
                    <span className="company-plan-select-indicator">
                        {isSelected && <CheckOutlined />}
                    </span>
                </div>
                <h3>{plan.name}</h3>
                <p className="company-plan-description">{plan.description}</p>
                <div className="company-plan-price-block">
                    {monthlyPrice === null ? (
                        <>
                            <span className="company-plan-price">Free</span>
                            <span className="company-plan-price-note">for {trialDays} days</span>
                        </>
                    ) : (
                        <>
                            <span className="company-plan-price">{formatCurrencyGbp(monthlyPrice)}</span>
                            <span className="company-plan-price-period">/month</span>
                            <span className="company-plan-price-note">
                                {billingCycle === 'annual'
                                    ? `Billed ${formatCurrencyGbp(plan.annualPriceGbp || 0)} yearly`
                                    : 'Billed monthly (+10%)'}
                            </span>
                        </>
                    )}
                </div>
                <div className="company-plan-divider" />
                <ul className="company-plan-features">
                    {plan.features.map((feature) => (
                        <li key={feature}><CheckOutlined /><span>{feature}</span></li>
                    ))}
                </ul>
                <span className="company-plan-footnote">{plan.footnote}</span>
            </div>
        );
    };

    return (
        <div className="login-view company-creation-page">
            <main className={`company-creation-shell company-creation-shell--${step}`}>
                <img src={loginImage} alt="e-nano" className="company-creation-logo" />

                {step === 'plan' && (
                    <section className="company-creation-panel company-creation-plan-panel">
                        <header className="company-creation-header">
                            <Button
                                type="text"
                                shape="circle"
                                icon={<ArrowLeftOutlined />}
                                aria-label="Back to company selection"
                                className="company-creation-back"
                                onClick={() => navigate('/select-company')}
                            />
                            <div>
                                <span className="company-creation-eyebrow">Create your workspace</span>
                                <Title level={1}>Choose the plan that fits</Title>
                                <Text>Your plan belongs to the company—not your personal user account.</Text>
                            </div>
                        </header>

                        <div className="company-plan-toolbar">
                            <div>
                                <strong>Simple pricing</strong>
                                <span>Change or upgrade your plan later.</span>
                            </div>
                            <Segmented
                                value={billingCycle}
                                onChange={(value) => setBillingCycle(value as BillingCycle)}
                                options={[
                                    { label: 'Annual · save 10%', value: 'annual' },
                                    { label: 'Monthly', value: 'monthly' },
                                ]}
                            />
                        </div>

                        {error && <Alert message={error} type="error" showIcon className="company-creation-alert" />}

                        {plansLoading ? (
                            <div className="company-plans-loading"><Spin size="large" /></div>
                        ) : (
                            <div className="company-plan-grid" role="radiogroup" aria-label="Company subscription plan">
                                {plans.map(renderPlanCard)}
                            </div>
                        )}

                        <footer className="company-plan-footer">
                            <div className="company-plan-footer-selection">
                                <SafetyCertificateOutlined />
                                <div>
                                    <span>{selectedPlanOption ? 'Selected plan' : 'No plan selected'}</span>
                                    <strong>{selectedPlanOption?.name || 'Choose a plan above'}</strong>
                                </div>
                            </div>
                            <Button
                                type="primary"
                                size="large"
                                disabled={!selectedPlan}
                                onClick={continueWithPlan}
                            >
                                Continue to company details
                            </Button>
                        </footer>
                    </section>
                )}

                {step === 'company' && (
                    <section className="company-creation-panel company-creation-details-panel">
                        <header className="company-creation-header company-creation-header--compact">
                            <Button
                                type="text"
                                shape="circle"
                                icon={<ArrowLeftOutlined />}
                                aria-label="Back to plan selection"
                                className="company-creation-back"
                                onClick={() => setStep('plan')}
                            />
                            <div>
                                <span className="company-creation-eyebrow">Company details</span>
                                <Title level={2}>Name your workspace</Title>
                                <Text>This company will be linked to your verified user account.</Text>
                            </div>
                        </header>

                        <div className="company-selected-plan">
                            <div>
                                <span>Selected plan</span>
                                <strong>{selectedPlanOption?.name}</strong>
                            </div>
                            <Button type="link" onClick={() => setStep('plan')}>Change plan</Button>
                        </div>

                        {error && <Alert message={error} type="error" showIcon className="company-creation-alert" />}

                        <Form form={form} layout="vertical" size="large" onFinish={createCompany} requiredMark={false}>
                            <Form.Item
                                name="companyName"
                                label="Company name"
                                initialValue=""
                                rules={[
                                    { required: true, message: 'Please enter your company name.' },
                                    { min: 2, message: 'Company name must be at least 2 characters.' },
                                    { max: 100, message: 'Company name must not exceed 100 characters.' },
                                ]}
                            >
                                <Input
                                    prefix={<ShopOutlined />}
                                    placeholder="Enter your company name"
                                    autoComplete="off"
                                    autoFocus
                                />
                            </Form.Item>
                            <Form.Item name="companyAddress" label="Company address (optional)">
                                <Input.TextArea rows={3} placeholder="Street, city, postcode, country" autoComplete="off" />
                            </Form.Item>
                            <Button type="primary" htmlType="submit" loading={submitting} block size="large">
                                {selectedPlan === 'trial' ? 'Create company and start trial' : 'Create company and continue to checkout'}
                            </Button>
                        </Form>
                    </section>
                )}

                {step === 'complete' && (
                    <section className="company-creation-panel company-creation-complete-panel">
                        <div className="company-creation-success-icon"><CheckOutlined /></div>
                        <span className="company-creation-eyebrow">Setup complete</span>
                        <Title level={2}>Your company is ready</Title>
                        <Text>Select it as your active workspace to start using OSCAR.</Text>
                        <Button type="primary" size="large" onClick={() => navigate('/select-company', { replace: true })}>
                            Select company
                        </Button>
                    </section>
                )}
            </main>
        </div>
    );
};

export default CompanyCreation;
